import React, { Component, ReactNode } from 'react';
import { CaseFieldPanel, CasePanelField } from '../../../../domain/case/CaseDomains';
import { TransactionData, TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { query, repository } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import { FormicaProps, withFormica } from 'helpers/withFormica';
import { DateVariableProperties, VariableProperties } from '../../../../domain/datatype/VariablePropertiesDomains';
import DataType from '../../../../constant/DataType';
import DateFormatUtil from '../../../../util/DateFormatUtil';
import TreeNode from 'primereact/treenode';
import { generateUUID } from '../../../../util/Generator';
import { Tree } from 'primereact/tree';
import { Dialog } from 'primereact/dialog';
import CaseService from '../../../../service/CaseService';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import CustomLoading from '../../../custom/CustomLoading';
import Icon from 'icons/Icon';
import { TransactionVariablePanelExportJob } from '../../../../domain/job/JobDomain';
import MessageUtil from '../../../../util/MessageUtil';
import JobService from '../../../../service/JobService';
import eq = query.eq;
import ComponentName from '../../../../constant/ComponentName';
import LanguageUtil from '../../../../util/LanguageUtil';

class CaseDetailVariablePanelComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  public render(): ReactNode {
    const { panel, index } = this.props;
    const { structureVisible, structureData, loading, panels } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div
        className={`h-[416px] w-full flex flex-col bg-[#FFFFFF] border-[#E0E4E8] border-[1px] rounded-[10px] ${panel.gridType == 'even' ? 'col-span-2' : 'col-span-1'} min-h-[200px]`}
        data-testid={`${ComponentName.CASE}_detail_variable_panel_${index}_container`}
      >
        <div className='flex justify-between items-center h-[50px] w-full px-4 border-b-[1px] border-[#E0E4E8] rounded-t-[10px] bg-[#F9FAFB]'>
          <span className={'text-primary'} data-testid={`${ComponentName.CASE}_detail_variable_panel_${index}_title`}>
            {panel.title}
          </span>
          <div
            onClick={this.exportPanel}
            className='cursor-pointer relative group border rounded border-[#E0E4E8] min-w-[28px] h-[28px] flex items-center justify-center bg-white hover:bg-f-gray active:bg-[#458EF714] active:border-[#458EF7]'
            data-testid={`${ComponentName.CASE}_detail_variable_panel_${index}_export`}
          >
            <Icon icon={'export'} size={16} color={'#4F4F4F'} className={'hover:text-primary hover:cursor-pointer flex items-center justify-center'} />
          </div>
        </div>
        <div className='w-full h-[calc(100%-50px)] flex flex-col gap-y-1 overflow-y-scroll'>
          {panels?.length == 0 && (
            <div className={'flex items-center justify-center w-full h-full'}>
              <Icon icon={'no-folder'} size={60}></Icon>
            </div>
          )}
          {panels?.map((panel: { key: string; value: any }, _index) => {
            return (
              <div
                key={_index}
                className='flex rounded shrink-0 box-border h-[44px] 2xl:h-[52px] w-full bg-white border-b px-5 py-2 last:border-none odd:bg-[#F9FAFB3D] items-center gap-x-4 justify-between'
              >
                <p
                  className='flex text-[#607080] w-1/3 custom-scroll overflow-x-auto overflow-y-hidden whitespace-nowrap'
                  data-testid={`${ComponentName.CASE}_detail_variable_panel_${index}_field_${_index}_key`}
                >
                  {panel.key}
                </p>
                <p
                  className='flex text-[#4F4F4F] w-2/3 custom-scroll overflow-x-auto overflow-y-hidden whitespace-nowrap'
                  data-testid={`${ComponentName.CASE}_detail_variable_panel_${index}_field_${_index}_value`}
                >
                  {this.parseDisplayValue(panel.value) || 'N/A'}
                </p>
              </div>
            );
          })}
        </div>
        <Dialog
          className={'h-1/2 w-1/3'}
          headerClassName={'!pb-0'}
          closable={false}
          header={<span className={'text-[15px] text-normal'}>Structure Items</span>}
          dismissableMask
          closeOnEscape
          visible={structureVisible}
          onHide={this.structureModalClose}
        >
          <Tree value={structureData} className={'border-none h-full w-full text-[13px] p-0 pt-2'} />
        </Dialog>
      </div>
    );
  }

  public async componentDidMount() {
    this.buildData();
  }

  private buildData = async () => {
    const { router, panel, transactionData } = this.props;
    const previousSearchParams = new URLSearchParams(router.params.id);
    const transactionName: string | null = previousSearchParams.get('transaction');
    const transaction: TransactionJsonModel | undefined = await this.repository.find<TransactionJsonModel>(
      query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', transactionName)).build(),
    );
    const enrichmentFields = panel.fields.filter((e) => e.type == 'EnrichmentVariable');
    let enrichmentValues: Record<string, any> = {};
    if (enrichmentFields.length > 0 && transactionData) {
      enrichmentValues = await this.caseService.getEnrichmentVariable(
        enrichmentFields.map((e) => e.value),
        transactionData,
      );
    }
    const fieldPromises = panel.fields
      .map(async (field) => {
        try {
          return {
            key: field.title,
            value: field.type == 'EnrichmentVariable' ? JSON.stringify(enrichmentValues?.[field.value]?.data) : this.getValue(field, transaction),
          };
        } catch (e) {
          return undefined;
        }
      })
      .filter((v) => v != undefined) as Promise<any>[];
    const panels = await Promise.all(fieldPromises);
    this.setState({ transaction, panels, loading: false });
  };

  private exportPanel = async () => {
    const { panels } = this.state;
    const sanitizedPanels = panels.map((panel) => ({
      key: panel.key,
      value: typeof panel.value === 'object' ? JSON.stringify(panel.value) : panel.value,
    }));
    try {
      const job: TransactionVariablePanelExportJob = {
        type: 'CASE_VARIABLE_PANEL_EXPORT',
        timestamp: Date.now(),
        status: 'PENDING',
        panels: sanitizedPanels,
        extension: 'csv',
        path: '',
      };
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.export' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.success.exportScheduled', defaultMessage: 'Export Scheduled' })}</div>,
      });
    } catch (e) {
      console.error(e);
    }
  };

  private getValue = (field: CasePanelField, transaction: TransactionJsonModel | undefined) => {
    const { transactionData } = this.props;
    const value = transactionData?.variables[field.value];
    if (value == undefined) return '';
    if (transaction) {
      const variable = transaction.variables.find((v) => v.name == field.value);
      if (variable) {
        return this.parseValue(value, variable.properties);
      }
      return value;
    }
  };

  private parseDisplayValue = (value: any): ReactNode => {
    if (typeof value === 'object') {
      return (
        <a className={'text-[#458EF7] cursor-pointer'} onClick={() => this.setState({ structureVisible: true, structureData: this.getStructureData(value) })}>
          Items
        </a>
      );
    }
    return value;
  };

  private parseValue = (value: any, properties: VariableProperties): ReactNode => {
    switch (properties.dataType) {
      case DataType.STRING:
        return value;
      case DataType.NUMBER:
        return (value as number)?.toFixed(2);
      case DataType.DATE:
        return DateFormatUtil.formatForTable(value, (properties as DateVariableProperties).format);
      case DataType.BOOLEAN:
        return value || value == 'true' ? 'True' : 'False';
      case DataType.OBJECT:
      case DataType.ARRAY:
      case DataType.STRUCTURE:
        return value;
      default:
        return value;
    }
  };

  private structureModalClose = () => {
    const { structureVisible } = this.state;
    this.setState({ structureVisible: !structureVisible });
  };

  private getStructureData = (data: any): TreeNode[] => {
    let result: TreeNode[] = [];
    for (const key in data) {
      result = result.concat(this.convertItem(data[key], key));
    }
    return result;
  };

  private convertItem(item: any, key: any): TreeNode[] {
    let newItem: TreeNode = {
      key: generateUUID(),
      label: key,
      children: [],
    };
    if (Array.isArray(item)) {
      item.forEach((subItem, index) => {
        const subKey = `${index}`;
        newItem.children = newItem?.children?.concat(this.convertItem(subItem, subKey));
      });
    } else if (typeof item === 'object') {
      for (const subKey in item) {
        newItem.children = newItem?.children?.concat(this.convertItem(item[subKey], subKey));
      }
    } else {
      newItem.label = `${key}: ${item}`;
    }
    return [newItem];
  }
}

interface Props extends FormicaProps {
  panel: CaseFieldPanel;
  transactionData: TransactionData | undefined;
  index: number;
}

class State {
  transaction: TransactionJsonModel | undefined;
  structureVisible: boolean = false;
  structureData: TreeNode[] = [];
  loading: boolean = true;
  panels: { key: string; value: any }[] = [];
}

export default withFormica(CaseDetailVariablePanelComponent);
